import { browser } from "$app/environment";

/**
 * The stored promo code, and the rule for how long it keeps auto-applying.
 *
 * The signup modal writes the code the customer earned
 * into localStorage, and both checkout hand-offs (product page buy-now, cart
 * drawer) put it on the Shopify checkout URL as ?discount=. Nothing ever
 * removed it, so a browser that once earned a 10-off code carried it into
 * every future checkout, forever. The codes have no usage limit, no expiry and
 * no once-per-customer flag in Shopify, so Shopify honoured every one.
 *
 * "Clear it after the order" is not available to us: the purchase happens on
 * Shopify's domain and only reaches us as a server-side webhook, and a cart
 * that has already converted still returns live from the Storefront API
 * (verified on order #4053), so the client cannot tell a bought cart from an
 * abandoned one.
 *
 * What the client does know is that it has already handed the code to
 * checkout. So: auto-apply it, then keep applying for a grace window - a
 * customer who bounces off the checkout form and comes back still gets their
 * discount - and drop it once the window closes.
 */

const CODE_KEY = "promoDiscountCode";
const FIRST_APPLIED_KEY = "promoDiscountCodeFirstAppliedAt";

/** Long enough to cover abandon-and-return; our own returns came back inside a minute. */
export const PROMO_GRACE_MS = 24 * 60 * 60 * 1000;

/** @returns {string|null} */
function read(key) {
  if (!browser) return null;
  try {
    return localStorage.getItem(key);
  } catch {
    // Storage blocked (private mode, cookie settings): behave as if unset.
    return null;
  }
}

function write(key, value) {
  if (!browser) return;
  try {
    localStorage.setItem(key, value);
  } catch {
    /* nothing to do - the code just won't survive this page */
  }
}

function drop(key) {
  if (!browser) return;
  try {
    localStorage.removeItem(key);
  } catch {
    /* nothing to do */
  }
}

/**
 * The code to auto-apply at checkout, or null.
 *
 * Expires the stored code in place once the grace window has passed, so the
 * next hand-off is clean without needing a separate cleanup pass.
 *
 * @param {number} [now]
 * @returns {{ code: string, expired: false } | { code: null, expired: boolean }}
 */
export function takePromoCode(now = Date.now()) {
  const code = read(CODE_KEY);
  if (!code) return { code: null, expired: false };

  // Explicit null check, not truthiness: a stamp of 0 is a real timestamp and
  // must not read as "never applied".
  const stored = read(FIRST_APPLIED_KEY);
  const firstApplied = stored === null ? null : Number(stored);
  const stamped = firstApplied !== null && Number.isFinite(firstApplied);

  if (stamped && now - firstApplied > PROMO_GRACE_MS) {
    drop(CODE_KEY);
    drop(FIRST_APPLIED_KEY);
    return { code: null, expired: true };
  }

  // First hand-off: start the window now.
  if (!stamped) write(FIRST_APPLIED_KEY, String(now));

  return { code, expired: false };
}

/**
 * The code takePromoCode would hand over, without handing it over: no grace
 * window is started and nothing is dropped. For a checkout the code cannot
 * apply to (ledger 2026-10-14-108), where taking it would start the 24 h
 * window on an order Shopify refuses it for.
 *
 * @param {number} [now]
 * @returns {string|null}
 */
export function peekPromoCode(now = Date.now()) {
  const code = read(CODE_KEY);
  if (!code) return null;

  const stored = read(FIRST_APPLIED_KEY);
  const firstApplied = stored === null ? null : Number(stored);
  if (
    firstApplied !== null &&
    Number.isFinite(firstApplied) &&
    now - firstApplied > PROMO_GRACE_MS
  ) {
    return null;
  }
  return code;
}

/**
 * Store a code the customer just earned, and reset any window a previous code
 * had started - a newly earned code deserves its own full grace period.
 *
 * @param {string} code
 */
export function storePromoCode(code) {
  if (!code) return;
  write(CODE_KEY, code);
  drop(FIRST_APPLIED_KEY);
}
